import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { logManualWorkout } from 'src/services/workouts.service';
import { StrongifierCompletedWorkoutV2 } from 'src/models/strongifier-completed-workout-v2';

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Run', label: 'Run' },
  { value: 'TrailRun', label: 'Trail Run' },
  { value: 'Ride', label: 'Ride' },
  { value: 'MountainBikeRide', label: 'Mountain Bike Ride' },
  { value: 'GravelRide', label: 'Gravel Ride' },
  { value: 'Swim', label: 'Swim' },
  { value: 'Walk', label: 'Walk' },
  { value: 'Hike', label: 'Hike' },
  { value: 'WeightTraining', label: 'Strength' },
  { value: 'Yoga', label: 'Yoga' },
  { value: 'Rowing', label: 'Rowing' },
  { value: 'Elliptical', label: 'Elliptical' },
  { value: 'Workout', label: 'Workout' },
];

const GYM_TYPES = ['WeightTraining', 'Yoga', 'Workout'];
const RUN_TYPES = ['Run', 'TrailRun'];

/** Backend-exact name-flag detection (activity_utils / workout_analysis_v2) */
const isNameFlagged = (name: string): boolean =>
  /threshold test|threshold run test|race|maximum effort|max effort/i.test(name);

const toLocalInputValue = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

interface LogWorkoutModalProps {
  onClose: () => void;
  onLogged: (workout: StrongifierCompletedWorkoutV2) => void;
}

export const LogWorkoutModal: React.FC<LogWorkoutModalProps> = ({ onClose, onLogged }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activityType, setActivityType] = useState('Run');
  const [name, setName] = useState('');
  const [dt, setDt] = useState(toLocalInputValue(new Date()));
  const [durH, setDurH] = useState('');
  const [durM, setDurM] = useState('');
  const [durS, setDurS] = useState('');
  const [distance, setDistance] = useState('');
  const [unit, setUnit] = useState<'km' | 'mi'>('km');
  const [hr, setHr] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const typeLabel = TYPE_OPTIONS.find((t) => t.value === activityType)?.label ?? activityType;
  const flagged = isNameFlagged(name);
  const isRunType = RUN_TYPES.includes(activityType);
  // Eligibility check in canonical km, whatever the entry unit
  const distanceRaw = parseFloat(distance);
  const distanceKmLive = unit === 'mi' ? distanceRaw * 1.60934 : distanceRaw;
  const eligible = flagged && isRunType && (isNaN(distanceKmLive) || distanceKmLive > 1.5);

  const submit = async () => {
    const errs: Record<string, string> = {};
    const t = dt ? new Date(dt).getTime() : NaN;
    if (!dt || isNaN(t)) errs.dt = 'Enter a date and time.';
    else if (t > Date.now() + 10 * 60 * 1000) errs.dt = 'Can’t be in the future.';
    const dh = parseFloat(durH) || 0;
    const dm = parseFloat(durM) || 0;
    const ds = parseFloat(durS) || 0;
    const dur = dh * 60 + dm + ds / 60;
    if (durH === '' && durM === '' && durS === '') errs.dur = 'Enter a duration.';
    else if (dh < 0 || dm < 0 || ds < 0 || dur <= 0) errs.dur = 'Duration must be greater than 0.';
    else if (dur > 1440) errs.dur = 'Max is 24 hours.';
    const distRaw = parseFloat(distance);
    const distKm = unit === 'mi' ? distRaw * 1.60934 : distRaw;
    if (distance === '' || isNaN(distRaw) || distRaw < 0) errs.dist = 'Enter a distance — 0 is valid.';
    else if (distKm > 500) errs.dist = unit === 'mi' ? 'Max is 310 mi.' : 'Max is 500 km.';
    if (hr !== '') {
      const hrNum = parseFloat(hr);
      if (isNaN(hrNum) || hrNum < 25 || hrNum > 250) errs.hr = 'Must be 25–250 bpm.';
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    setServerError(null);
    try {
      const token = await getAccessTokenSilently();
      const resp = await logManualWorkout(token, {
        activity_type: activityType,
        local_datetime: dt.length === 16 ? `${dt}:00` : dt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration_minutes: dur,
        distance_km: Math.round(distKm * 1000) / 1000,
        workout_name: name.trim() || undefined,
        average_heart_rate: hr !== '' ? parseFloat(hr) : undefined,
      });
      if (resp.error) {
        setServerError(resp.error.message);
      } else if (resp.data) {
        onLogged(resp.data);
      }
    } catch (e: any) {
      setServerError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (err?: string) =>
    `w-full box-border bg-[#10151f] border rounded-lg px-3 py-2 text-[13.5px] text-[#e8ecf4] outline-none focus:border-[#5661f0] ${
      err ? 'border-[#8a3a34]' : 'border-[#2e3850]'
    }`;
  const labelClass = 'text-[11px] tracking-[1.1px] uppercase text-[#6b7688] mb-1.5';

  return (
    <div
      onClick={submitting ? undefined : onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{ background: 'rgba(5,7,11,.72)', backdropFilter: 'blur(3px)', padding: '48px 20px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#1a2130] border border-[#2a3550] rounded-xl w-[520px] max-w-full"
        style={{ boxShadow: '0 24px 64px rgba(0,0,0,.5)', colorScheme: 'dark' }}
      >
        <div className="flex items-center justify-between px-6 py-[18px] border-b border-[#232c3e]">
          <div className="font-['Chakra_Petch'] text-[17px] font-semibold tracking-[.4px] text-[#e8ecf4]">
            Log a manual workout
          </div>
          <div
            onClick={submitting ? undefined : onClose}
            className="w-7 h-7 rounded-[7px] flex items-center justify-center cursor-pointer text-[#6b7688] hover:bg-[#232c3e] hover:text-[#e8ecf4]"
          >
            ✕
          </div>
        </div>
        <div className="px-6 pt-5 pb-1 flex flex-col gap-4">
          <div>
            <div className={labelClass}>Activity type</div>
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              className={inputClass() + ' text-[14px] py-2.5'}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div className={labelClass}>
              Workout name <span className="normal-case tracking-normal text-[#46527a]">· optional</span>
            </div>
            <input
              type="text"
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={typeLabel}
              className={inputClass() + ' text-[14px] py-2.5'}
            />
            {eligible && (
              <div className="flex items-center gap-1.5 text-[12px] text-[#3ec9a7] mt-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3ec9a7" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"></path></svg>
                Flagged as a max effort — eligible to update your threshold.
              </div>
            )}
            {!eligible && isRunType && (
              <div className="text-[12px] text-[#6b7688] mt-1.5">
                {flagged
                  ? 'Threshold updates need a distance over 1.5 km.'
                  : 'Include “race”, “threshold test” or “max effort” in the name to make this run eligible to update your threshold.'}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3.5">
            <div>
              <div className={labelClass}>Date &amp; time</div>
              <input
                type="datetime-local"
                value={dt}
                max={toLocalInputValue(new Date(Date.now() + 10 * 60 * 1000))}
                onChange={(e) => setDt(e.target.value)}
                className={inputClass(errors.dt)}
              />
              {errors.dt && <div className="text-[12px] text-[#e0655c] mt-1">{errors.dt}</div>}
            </div>
            <div>
              <div className={labelClass}>Duration</div>
              <div className="grid grid-cols-3 gap-[7px]">
                <div>
                  <input
                    type="number" min={0} max={24} step={1}
                    value={durH}
                    onChange={(e) => setDurH(e.target.value)}
                    placeholder="0"
                    className={inputClass(errors.dur) + ' px-2.5'}
                  />
                  <div className="text-[10.5px] text-[#46527a] mt-[3px] text-center">hr</div>
                </div>
                <div>
                  <input
                    type="number" min={0} step={1}
                    value={durM}
                    onChange={(e) => setDurM(e.target.value)}
                    placeholder="45"
                    className={inputClass(errors.dur) + ' px-2.5'}
                  />
                  <div className="text-[10.5px] text-[#46527a] mt-[3px] text-center">min</div>
                </div>
                <div>
                  <input
                    type="number" min={0} max={59} step={1}
                    value={durS}
                    onChange={(e) => setDurS(e.target.value)}
                    placeholder="0"
                    className={inputClass(errors.dur) + ' px-2.5'}
                  />
                  <div className="text-[10.5px] text-[#46527a] mt-[3px] text-center">sec</div>
                </div>
              </div>
              {errors.dur && <div className="text-[12px] text-[#e0655c] mt-1">{errors.dur}</div>}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] tracking-[1.1px] uppercase text-[#6b7688]">Distance</div>
                <div className="flex border border-[#2e3850] rounded-md overflow-hidden">
                  <div
                    onClick={() => setUnit('km')}
                    className="text-[10.5px] font-semibold px-[9px] py-[3px] cursor-pointer"
                    style={{ color: unit === 'km' ? '#ffffff' : '#6b7688', background: unit === 'km' ? '#5661f0' : 'transparent' }}
                  >
                    km
                  </div>
                  <div
                    onClick={() => setUnit('mi')}
                    className="text-[10.5px] font-semibold px-[9px] py-[3px] cursor-pointer border-l border-[#2e3850]"
                    style={{ color: unit === 'mi' ? '#ffffff' : '#6b7688', background: unit === 'mi' ? '#5661f0' : 'transparent' }}
                  >
                    mi
                  </div>
                </div>
              </div>
              <input
                type="number" min={0} max={unit === 'mi' ? 310 : 500} step={0.1}
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                placeholder="8.0"
                className={inputClass(errors.dist)}
              />
              {errors.dist && <div className="text-[12px] text-[#e0655c] mt-1">{errors.dist}</div>}
              {!errors.dist && GYM_TYPES.includes(activityType) && (
                <div className="text-[12px] text-[#6b7688] mt-1">0 is fine for gym entries.</div>
              )}
            </div>
            <div>
              <div className={labelClass}>
                Avg heart rate <span className="normal-case tracking-normal text-[#46527a]">· optional, bpm</span>
              </div>
              <input
                type="number" min={25} max={250} step={1}
                value={hr}
                onChange={(e) => setHr(e.target.value)}
                placeholder="140"
                className={inputClass(errors.hr)}
              />
              {errors.hr && <div className="text-[12px] text-[#e0655c] mt-1">{errors.hr}</div>}
            </div>
          </div>
          <div className="text-[12px] leading-[1.5] text-[#6b7688]">
            Heart rate is attributed to zones as one whole effort.
          </div>
          {serverError && (
            <div className="text-[12.5px] text-[#e0655c]">Error: {serverError}</div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3.5 px-6 pt-4 pb-5">
          <div className="text-[11.5px] text-[#46527a]">
            Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone} · detected
          </div>
          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              disabled={submitting}
              className="bg-transparent text-[#aab6d0] border border-[#2e3850] rounded-lg px-4 py-[9px] text-[14px] font-semibold cursor-pointer hover:border-[#46527a] hover:text-[#e8ecf4] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="bg-[#5661f0] text-white border-0 rounded-lg px-[18px] py-[9px] text-[14px] font-semibold cursor-pointer hover:bg-[#4650e0] disabled:opacity-50"
            >
              {submitting ? 'Logging…' : 'Log workout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
